import { Slide, Slider, SliderList } from '@ldp-web/component-library/ui/molecules/slider';
import { Banner, BannerSliderWrapper } from '@ldp-web/component-library/ui/molecules/banner';
import React, { FC, useCallback, useState, useEffect } from 'react';
import { useSimpleTranslation } from '@ldp/simple-i18n';
import { BannerComponentProps, CTAOptionalLink, TBannerModal } from './banner-carousel.component.interface';
import { Badge } from '@ldp-web/component-library/types';
import { useSDMRouterContext } from '@ldp/sdm-contexts';
import { useRedirectLink } from '../../utils';
import {
  TSDMBannerContextAnalytics,
  TSDMCarouselAnalyticTypes,
  TSDMComponentAnalyticsObject,
} from '@ldp/sdm-analytics-utils';
import { useAnalyticsContext, useFeatureFlags } from '@ldp/common-contexts';
import '@splidejs/splide/dist/css/splide.min.css';

export interface IBannerCarouselComponentProps {
  banners: BannerComponentProps[];
  _allBanners?: BannerComponentProps[];
  contentId?: string;
  componentId?: string;
  variantId?: string;
  analytics?: TSDMComponentAnalyticsObject;
}

/** Renderer for BannerCarouselComponent - a variant of banner component in web component library */
export const BannerCarouselComponent: FC<IBannerCarouselComponentProps> = (props) => {
  const { banners: initialBanners, _allBanners, contentId, componentId, analytics } = props;
  const [banners, setBanners] = useState(initialBanners);
  
  useEffect(() => {
    if (_allBanners && _allBanners.length > initialBanners.length) {
      setBanners(_allBanners);
    }
  }, [_allBanners, initialBanners]);

  const i18n = useSimpleTranslation();
  const router = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);
  const { trackEvent } = useAnalyticsContext();
  const { getFeatureFlags } = useFeatureFlags();
  const { EnableHeliosSnowplow: snowplowEnabled } = getFeatureFlags();

  const bannerLength = banners.length;
  const showSliderComps = bannerLength > 1;
  const a11yMessages = {
    first: i18n('a11ySliderFirstLabel'),
    last: i18n('a11ySliderLastLabel'),
    prev: i18n('a11ySliderPrevLabel'),
    next: i18n('a11ySliderNextLabel'),
    slideX: i18n('a11ySlideXLabel'),
    select: i18n('a11ySliderSelectLabel'),
    slideLabel: i18n('a11ySlideLabel'),
  };

  const trackFnWrapper = useCallback((type: string, context: unknown): void => {
    let componentContext, clickedBanner;
    const contextData = context as TSDMBannerContextAnalytics;
    const hierarchyVal = [componentId || ''];
    const dsModelData = analytics?.dsModel;
    const componentContextData = {
      type: componentId || TSDMCarouselAnalyticTypes.BANNER_TYPE,
      engine: dsModelData?.engine,
      parent_id: contentId,
    };
    if (contextData) {
      if (contextData.actionType === 'view') {
        const bannerIndex = contextData?.slideNewIndex || 0;
        clickedBanner = banners[bannerIndex];
        componentContext = [
          {
            ...componentContextData,
            type: clickedBanner?.componentId || TSDMCarouselAnalyticTypes.BANNER_TYPE,
            id: clickedBanner?.contentId || '',
            name: clickedBanner?.headline,
            position: bannerIndex + 1,
            hierarchy: hierarchyVal.concat(clickedBanner?.headline),
          },
        ];
      } else {
        const clickedBanner = banners.filter((banner) => banner.headline === contextData.name)[0];
        const clickActionType = 'cta-click';
        const ctaValue = [clickedBanner?.callToAction1, clickedBanner?.callToAction2, clickedBanner?.callToAction3]
          .map((cta, index) => {
            return cta?.text === contextData?.clickAction ? `${clickActionType}${index + 1}` : null;
          })
          .filter((value) => {
            return value != null;
          })[0];

        const clickedPos = ctaValue?.charAt(ctaValue?.length - 1);

        const bannerCompContext = {
          ...componentContextData,
          type: clickedBanner?.componentId || TSDMCarouselAnalyticTypes.BANNER_TYPE,
          id: clickedBanner?.contentId || '',
          name: contextData.name,
          position: clickedPos ? parseInt(clickedPos) : 0,
          hierarchy: hierarchyVal.concat(clickedBanner?.headline),
          click_element: contextData.clickAction,
          click_action: clickActionType,
          click_url: contextData.clickUrl,
        };
        componentContext = {
          ...bannerCompContext,
        };
      }
      trackEvent('heliosBannerCarousel', {
        event: type,
        heliosSnowplow: snowplowEnabled,
        heliosAnalytics: true,
        component: componentContext,
        customEngagement: {
          action: contextData.actionType,
        },
        dsModel: {
          type: dsModelData?.profileName || '',
          value: dsModelData?.profileVersion || '',
        },
      });
    }
  }, [analytics, banners, componentId, contentId, snowplowEnabled, trackEvent]);

  if (banners?.length === 1) {
    const {
      backgroundColour,
      buttonBackgroundColour,
      buttonTextColour,
      headline,
      superHeadline,
      logo1,
      logo2,
      badge,
      textColour,
      ctaLinkColour,
      desktopImage,
      tabletImage,
      mobileImage,
      displayText1,
      callToAction1,
      callToAction2,
      callToAction3,
      callToActionLink,
      stamp,
      stampPlacement,
    } = banners[0];

    let linkData, linkModalData;
    if (callToActionLink && Object.prototype.hasOwnProperty.call(callToActionLink, 'modalTitle')) {
      linkModalData = callToActionLink;
    } else linkData = callToActionLink;

    return (
      <BannerSliderWrapper>
        <Banner
          key={`banner-${contentId}-${0}`}
          variant="full"
          title={headline}
          bodyText={displayText1}
          desktopImage={desktopImage}
          tabletImage={tabletImage}
          mobileImage={mobileImage}
          primaryLogo={logo1}
          secondaryLogo={logo2}
          promoCTA1={callToAction1}
          promoCTA2={callToAction2}
          promoCTA3={callToAction3}
          link={linkData as CTAOptionalLink}
          linkModal={linkModalData as TBannerModal}
          badge={badge as Badge}
          superHeadline={superHeadline}
          buttonClickHandler={redirectToPage}
          titleColor={textColour}
          bodyTextColor={textColour}
          superHeadlineColor={textColour}
          backgroundColor={backgroundColour}
          innerContentBackgroundColor={backgroundColour}
          buttonBackgroundColor={buttonBackgroundColour}
          buttonTextColor={buttonTextColour}
          ctaLinkColor={ctaLinkColour}
          trackingFn={trackFnWrapper}
          stamp={stamp}
          stampPlacement={stampPlacement}
        />
      </BannerSliderWrapper>
    );
  }

  return (
    <BannerSliderWrapper>
      <Slider
        sliderContainerAriaLabel={i18n('a11yBanner')}
        a11yI18nMessages={a11yMessages}
        data-testid="banner-carousel"
        showArrows={showSliderComps}
        showPagination={showSliderComps}
        trackingFn={trackFnWrapper}
        isBannerSlider={false}
      >
        <SliderList>
          {banners?.map((banner: BannerComponentProps, i: number) => {
            const {
              backgroundColour,
              buttonBackgroundColour,
              buttonTextColour,
              headline,
              superHeadline,
              logo1,
              logo2,
              badge,
              textColour,
              ctaLinkColour,
              desktopImage,
              tabletImage,
              mobileImage,
              displayText1,
              callToAction1,
              callToAction2,
              callToAction3,
              callToActionLink,
              stamp,
              stampPlacement,
            } = banner;
            let linkData, linkModalData;
            if (callToActionLink && Object.prototype.hasOwnProperty.call(callToActionLink, 'modalTitle')) {
              linkModalData = callToActionLink;
            } else linkData = callToActionLink;
            return (
              <Slide key={`slide-${contentId}-${i}`}>
                <Banner
                  key={`banner-${contentId}-${i}`}
                  variant="full"
                  title={headline}
                  bodyText={displayText1}
                  desktopImage={desktopImage}
                  tabletImage={tabletImage}
                  mobileImage={mobileImage}
                  primaryLogo={logo1}
                  secondaryLogo={logo2}
                  promoCTA1={callToAction1}
                  promoCTA2={callToAction2}
                  promoCTA3={callToAction3}
                  link={linkData as CTAOptionalLink}
                  linkModal={linkModalData as TBannerModal}
                  badge={badge as Badge}
                  superHeadline={superHeadline}
                  buttonClickHandler={redirectToPage}
                  titleColor={textColour}
                  bodyTextColor={textColour}
                  superHeadlineColor={textColour}
                  backgroundColor={backgroundColour}
                  innerContentBackgroundColor={backgroundColour}
                  buttonBackgroundColor={buttonBackgroundColour}
                  buttonTextColor={buttonTextColour}
                  ctaLinkColor={ctaLinkColour}
                  trackingFn={trackFnWrapper}
                  stamp={stamp}
                  stampPlacement={stampPlacement}
                  isBannerSlider={false}
                />
              </Slide>
            );
          })}
        </SliderList>
      </Slider>
    </BannerSliderWrapper>
  );
};

export default BannerCarouselComponent;
